<script setup lang="ts">
import { computed, markRaw, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import DividerComponent from '@/components/ui/DividerComponent.vue';
import RefuseKycComponent from '@/components/user/RefuseKycComponent.vue';
import EditComplianceFlagsComponent from '@/components/user/EditComplianceFlagsComponent.vue';
import UserService from '@/services/user/userService.ts';
import LcbftService from '@/services/lcbft/lcbftService.ts';
import ClientExportService from '@/services/user/clientExportService.ts';
import KycComplianceFlagsService from '@/services/user/kycComplianceFlagsService.ts';
import { useAuthStore } from '@/stores/authStore.ts';
import { PriorServiceEnum } from '@/enums/clientDeclaration_enum.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import { displayToast } from '@/utils/toastUtils.ts';
import {
  formatItemValue,
  formatUserStatus,
  formatKycState,
  formatKycStatus,
  formatKnownOrRaw,
  formatLcbftClassification,
  formatDateOnly,
  formatFullDate,
  formatCurrency,
  formatPercentage,
} from '@/utils/formatters.ts';

import type { DataTableHeader } from 'vuetify/framework';
import type { B2CUser, KycRefuseRequest, UserStatusKey } from '@/types/services/user/user.type.ts';
import type { LcbftClassification } from '@/types/services/lcbft/lcbft.type.ts';
import type { ClientDeclaration } from '@/types/services/user/clientDeclaration.type.ts';
import type { InvestorForm } from '@/types/services/user/investorForm.type.ts';
import type { KycDocument } from '@/types/services/user/kycDocument.type.ts';
import type { KycComplianceFlags } from '@/types/services/user/kycComplianceFlags.type.ts';
import type { ModalOptions } from '@/types/stores/modalStore.type.ts';

const { t } = useI18n()
const route = useRoute()
const { smAndDown } = useDisplay()
const authStore = useAuthStore()

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission);
}

const step = ref<number>(1)
const loading = ref<boolean>(false)

const user = ref<B2CUser>(route.meta.user as B2CUser)
const lcbft = ref<LcbftClassification | null>((route.meta.lcbft as LcbftClassification | null) ?? null)
const clientDeclaration = ref<ClientDeclaration | null>((route.meta.clientDeclaration as ClientDeclaration | null) ?? null)
const investorForm = ref<InvestorForm | null>((route.meta.investorForm as InvestorForm | null) ?? null)
const kycDocuments = ref<KycDocument[]>((route.meta.kycDocuments as KycDocument[]) ?? [])

async function refreshUser(): Promise<void> {
  user.value = await UserService.getB2CUser(user.value.id)
}

const exporting = ref<boolean>(false)

async function exportClient(): Promise<void> {
  try {
    exporting.value = true
    await ClientExportService.exportClient(user.value.id)
  } finally {
    exporting.value = false
  }
}

function downloadKycDocument(kycDocument: KycDocument): void {
  if (!kycDocument.downloadUrl) {
    return
  }

  window.open(kycDocument.downloadUrl, '_blank')
}

async function updateStatus(status: UserStatusKey): Promise<void> {
  const username: string = `${user.value.firstName} ${user.value.lastName}`

  let title: string = t('common.userList.reactivateUser.title')
  let message: string = t('common.userList.reactivateUser.message', { username })
  let success: string = t('common.userList.reactivateSuccess')

  if (status === 'inactive') {
    title = t('common.userList.deactivateUser.title')
    message = t('common.userList.deactivateUser.message', { username })
    success = t('common.userList.deactivateSuccess')
  }

  const options: ModalOptions = { title, message }
  const modal = await displayModal(options)

  if (modal) {
    try {
      loading.value = true
      await UserService.updateUserStatus(user.value.id, status)
      displayToast(success, 'success')
      await refreshUser()
    } finally {
      loading.value = false
    }
  }
}

async function newKycRequest(): Promise<void> {
  const username: string = `${user.value.firstName} ${user.value.lastName}`

  const title: string = t('views.updateUser.newKyc.title')
  const message: string = t('views.updateUser.newKyc.message', { username })
  const success: string = t('views.updateUser.newKycSuccess')

  const options: ModalOptions = { title, message }
  const modal = await displayModal(options)

  if (modal) {
    try {
      loading.value = true
      await UserService.createKycRequest(user.value.id)
      displayToast(success, 'success')
      await refreshUser()
    } finally {
      loading.value = false
    }
  }
}

const resendingActivation = ref<boolean>(false)

async function resendActivation(isWeb: boolean): Promise<void> {
  const username: string = `${user.value.firstName} ${user.value.lastName}`

  const options: ModalOptions = {
    title: t('views.updateUser.resendActivation.title'),
    message: t(isWeb ? 'views.updateUser.resendActivation.messageWeb' : 'views.updateUser.resendActivation.messageMobile', { username })
  }
  const modal = await displayModal(options)

  if (modal) {
    try {
      resendingActivation.value = true
      await UserService.resendActivation(user.value.id, isWeb)
      displayToast(t('views.updateUser.resendActivationSuccess'), 'success')
    } finally {
      resendingActivation.value = false
    }
  }
}

async function approveKyc(): Promise<void> {
  const username: string = `${user.value.firstName} ${user.value.lastName}`

  const title: string = t('views.updateUser.approvedKyc.title')
  const message: string = t('views.updateUser.approvedKyc.message', { username })
  const success: string = t('views.updateUser.approvedKycSuccess')

  const options: ModalOptions = { title, message }
  const modal = await displayModal(options)

  if (modal) {
    try {
      loading.value = true
      await UserService.approveKyc(user.value.id)
      displayToast(success, 'success')
      await refreshUser()
    } finally {
      loading.value = false
    }
  }
}

async function refusedKyc(): Promise<void> {
  const modal = await displayModal({
    component: markRaw(RefuseKycComponent),
    props: {
      userName: user.value.lastName + ' ' + user.value.firstName
    },
  });

  if (modal && typeof modal === 'object') {
    const { reason } = modal as KycRefuseRequest;
    try {
      loading.value = true
      await UserService.refuseKyc(user.value.id, reason);
      displayToast(t('views.updateUser.refusalSuccess'), 'success');
      await refreshUser()
    } finally {
      loading.value = false
    }
  }
}

function getKycIcon(value: string): string {
  const val = value?.toUpperCase() || ''

  switch (val) {
    case 'PROCESSED':
    case 'APPROVED':
    case 'VALIDATED':
      return 'PhCheckCircle'

    case 'WAITING':
    case 'STARTED':
    case 'RUNNING':
    case 'PENDING':
      return 'PhClock'

    case 'FAILED':
    case 'ABORTED':
    case 'EXPIRED':
    case 'DELETED':
      return 'PhXCircle'

    case 'VOID':
      return 'PhWarningCircle'

    default:
      return 'PhQuestion'
  }
}

function getKycStatusIcon(status: string | null): string {
  switch (status) {
    case 'approved':
      return 'PhCheckCircle'
    case 'need_review':
      return 'PhWarningCircle'
    case 'rejected':
      return 'PhXCircle'
    default:
      return 'PhQuestion'
  }
}

function amlColor(value: string | null): string {
  if (value === 'positive') return 'red darken-2'
  if (value === 'negative') return 'green darken-2'
  return 'grey darken-1'
}

function boolLabel(value: boolean | null): string {
  if (value == null) return t('common.noData')
  return value ? t('common.yes') : t('common.no')
}

// Closed enums (client declaration) — no Dataleon-style drift, so a plain lookup is fine.
function cd(prefix: string, value: string | null): string {
  if (!value) return t('common.noData')
  return t(`views.detailUser.clientDeclaration.enums.${prefix}.${value}`)
}

const editingComplianceFlags = ref<boolean>(false)

async function editComplianceFlags(): Promise<void> {
  // No GET endpoint for these flags — the form always opens blank; the BO agent only checks the
  // signals they're recording during this review.
  const initialFlags: KycComplianceFlags = {
    documentsInForeignLanguage: false,
    refusedToCommunicateInformation: false,
    attitudeChangeDetected: false,
    priorSuspiciousActivityReport: false
  }

  const modal = await displayModal({
    component: markRaw(EditComplianceFlagsComponent),
    props: {
      userName: user.value.lastName + ' ' + user.value.firstName,
      initialFlags
    },
  });

  if (modal && typeof modal === 'object') {
    try {
      editingComplianceFlags.value = true
      await KycComplianceFlagsService.update(user.value.id, modal as KycComplianceFlags)
      lcbft.value = await LcbftService.getClassification(user.value.id)
      displayToast(t('views.detailUser.lcbft.editFlagsSuccess'), 'success')
    } finally {
      editingComplianceFlags.value = false
    }
  }
}

const recomputingLcbft = ref<boolean>(false)

async function recomputeLcbft(): Promise<void> {
  const options: ModalOptions = {
    title: t('views.detailUser.lcbft.recomputeConfirm.title'),
    message: t('views.detailUser.lcbft.recomputeConfirm.message')
  }
  const modal = await displayModal(options)

  if (modal) {
    try {
      recomputingLcbft.value = true
      lcbft.value = await LcbftService.recompute(user.value.id)
      displayToast(t('views.detailUser.lcbft.recomputeSuccess'), 'success')
    } finally {
      recomputingLcbft.value = false
    }
  }
}

const breakdownHeaders = computed<DataTableHeader[]>(() => [
  { title: t('views.detailUser.lcbft.table.criterion'), key: 'criterion', align: 'start', sortable: false },
  { title: t('views.detailUser.lcbft.table.value'), key: 'value', align: 'start', sortable: false },
  { title: t('views.detailUser.lcbft.table.level'), key: 'level', align: 'start', sortable: false },
  { title: t('views.detailUser.lcbft.table.points'), key: 'points', align: 'end', sortable: false },
])

const childrenHeaders = computed<DataTableHeader[]>(() => [
  { title: t('views.detailUser.clientDeclaration.children.table.lastName'), key: 'lastName', align: 'start', sortable: false },
  { title: t('views.detailUser.clientDeclaration.children.table.firstName'), key: 'firstName', align: 'start', sortable: false },
  { title: t('views.detailUser.clientDeclaration.children.table.birthDate'), key: 'birthDate', align: 'start', sortable: false },
])

const creditsHeaders = computed<DataTableHeader[]>(() => [
  { title: t('views.detailUser.clientDeclaration.credits.table.monthlyPayment'), key: 'monthlyPayment', align: 'start', sortable: false },
  { title: t('views.detailUser.clientDeclaration.credits.table.outstandingCapital'), key: 'outstandingCapital', align: 'start', sortable: false },
])

const answersHeaders = computed<DataTableHeader[]>(() => [
  { title: t('views.detailUser.investorForm.table.question'), key: 'question', align: 'start', sortable: false },
  { title: t('views.detailUser.investorForm.table.answer'), key: 'answer', align: 'start', sortable: false },
  { title: t('views.detailUser.investorForm.table.category'), key: 'category', align: 'start', sortable: false },
  { title: t('views.detailUser.investorForm.table.contribution'), key: 'weightedContribution', align: 'end', sortable: false },
  { title: t('views.detailUser.investorForm.table.weight'), key: 'weight', align: 'end', sortable: false },
])

const documentsHeaders = computed<DataTableHeader[]>(() => [
  { title: t('views.detailUser.kycDocuments.table.name'), key: 'name', align: 'start', sortable: false },
  { title: t('views.detailUser.kycDocuments.table.documentType'), key: 'documentType', align: 'start', sortable: false },
  { title: t('views.detailUser.kycDocuments.table.state'), key: 'state', align: 'start', sortable: false },
  { title: t('views.detailUser.kycDocuments.table.status'), key: 'status', align: 'start', sortable: false },
  { title: t('views.detailUser.kycDocuments.table.action'), key: 'actions', align: 'end', sortable: false, width: '100px' },
])
</script>

<template>
  <MainTitleComponent>
    {{ t('views.detailUser.title') }}

    <template #actions>
      <v-col cols="12" sm="auto">
        <v-btn :disabled="exporting" :loading="exporting" color="primary" variant="outlined" @click="exportClient()">
          {{ t('views.detailUser.buttons.export') }}
          <template #prepend><v-icon icon="PhFileXls" /></template>
        </v-btn>
      </v-col>
    </template>
  </MainTitleComponent>

  <v-stepper v-model="step" :elevation="0" :mobile="smAndDown">
    <v-stepper-header>
      <v-divider />
      <v-stepper-item :title="t('views.detailUser.stepper.step1')" :value="1" color="primary" editable edit-icon="" />
      <v-divider />
      <v-stepper-item :title="t('views.detailUser.stepper.step2')" :value="2" color="primary" editable edit-icon="" />
      <v-divider />
      <v-stepper-item :title="t('views.detailUser.stepper.step3')" :value="3" color="primary" editable edit-icon="" />
      <v-divider />
      <v-stepper-item :title="t('views.detailUser.stepper.step4')" :value="4" color="primary" editable edit-icon="" />
      <v-divider />
      <v-stepper-item :title="t('views.detailUser.stepper.step5')" :value="5" color="primary" editable edit-icon="" />
      <v-divider />
    </v-stepper-header>

    <v-stepper-window>
      <!-- STEP 1: Vue d'ensemble -->
      <v-stepper-window-item :value="1">
        <v-row align="center" class="details">
          <v-col cols="6">
            <p class="text-label">{{ t('views.userList.label.status') }}</p>
          </v-col>
          <v-col cols="6" class="text-right">
            <v-chip :color="formatUserStatus(user.status)" class="ma-2">
              {{ t('common.userList.status.' + user.status) }}
            </v-chip>
          </v-col>
        </v-row>

        <template v-if="user.duplicateReview">
          <v-alert class="mb-4" color="warning" variant="outlined" icon="PhWarning">
            <div class="d-flex flex-column flex-sm-row justify-space-between align-sm-center ga-2">
              <div>
                <p class="font-weight-bold">{{ t('views.detailUser.overview.duplicate.title') }}</p>
                <p class="mb-2">{{ t('views.detailUser.overview.duplicate.description') }}</p>
                <div
                  v-for="match in user.duplicateReview.matches"
                  :key="match.userId"
                  class="d-flex flex-wrap align-center ga-2 mb-1"
                >
                  <router-link :to="`/users/${match.userId}/detail`">
                    {{ `${match.firstName} ${match.lastName}`.trim() }} - {{ match.email }} (#{{ match.userId }})
                  </router-link>
                  <v-chip
                    :color="match.birthPlaceMatch === true ? 'error' : (match.birthPlaceMatch === false ? 'success' : 'warning')"
                    size="x-small"
                  >
                    {{ t(`views.detailUser.overview.duplicate.birthPlace.${match.birthPlaceMatch === true ? 'same' : (match.birthPlaceMatch === false ? 'different' : 'unknown')}`) }}
                  </v-chip>
                </div>
              </div>
              <v-btn
                v-if="hasPermission(['GET_IDENTITY_DUPLICATE_REVIEW'])"
                :to="`/identity-duplicate-reviews/${user.duplicateReview.id}`"
                size="small"
                variant="outlined"
              >
                {{ t('views.detailUser.overview.duplicate.viewReview') }}
              </v-btn>
            </div>
          </v-alert>
        </template>

        <DividerComponent />

        <v-row align="center">
          <v-col cols="12" class="d-flex align-center">
            <v-icon color="primary" class="mr-2" icon="PhUser" />
            {{ t('views.userList.subtitle.user') }}
          </v-col>
        </v-row>
        <v-row class="details">
          <v-col cols="12" sm="6" md="4">
            <p class="text-label">{{ t('views.updateUser.name') }}</p>
            <p class="text-value">{{ user.firstName + ' ' + user.lastName }}</p>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <p class="text-label">{{ t('views.detailUser.overview.usageName') }}</p>
            <p class="text-value">{{ formatItemValue(user.usageName) }}</p>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <p class="text-label">{{ t('views.updateUser.email') }}</p>
            <p class="text-value">{{ user.email }}</p>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <p class="text-label">{{ t('views.updateUser.phone') }}</p>
            <p class="text-value">{{ formatItemValue(user.phone) }}</p>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <p class="text-label">{{ t('views.updateUser.role') }}</p>
            <p class="text-value">{{ t(`common.role.${user.role}`) }}</p>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <p class="text-label">{{ t('views.updateUser.profileType') }}</p>
            <p class="text-value">{{ formatItemValue(user.profileType ? t(`views.updateUser.profile.${user.profileType}`) : null) }}</p>
          </v-col>
        </v-row>

        <DividerComponent />

        <template v-if="user.kyc">
          <v-row>
            <v-col cols="12" class="d-flex justify-space-between">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="primary" icon="PhShieldCheck" />
                {{ t('views.updateUser.dataleon') }}
              </div>

              <div class="d-flex align-center justify-space-between">
                <v-btn
                  v-if="user.kyc.status != 'approved' && user.kyc.status != null"
                  :disabled="loading"
                  :loading="loading"
                  class="mr-4"
                  color="error"
                  variant="outlined"
                  @click="refusedKyc()"
                >
                  {{ t('views.updateUser.buttons.refused_kyc') }}
                  <template #prepend><v-icon size="small" icon="PhProhibit" /></template>
                </v-btn>

                <v-btn
                  v-if="user.kyc.status != 'approved' && user.kyc.status != null"
                  :disabled="loading"
                  :loading="loading"
                  type="submit"
                  variant="outlined"
                  @click="approveKyc()"
                >
                  {{ t('views.updateUser.buttons.approved_kyc') }}
                  <template #prepend><v-icon size="small" icon="PhCheckCircle" /></template>
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-alert :color="formatKycState(user.kyc.state)" variant="outlined">
                <template #prepend><v-icon :icon="getKycIcon(user.kyc.state)" size="large" /></template>
                <div class="text-black">
                  <p class="text-label font-weight-bold">{{ t('views.updateUser.state') }}</p>
                  <p class="text-value">{{ formatKnownOrRaw('views.userList.kyc.state', user.kyc.state) }}</p>
                </div>
              </v-alert>
            </v-col>

            <v-col cols="12" v-if="user.kyc.status">
              <v-alert :color="formatKycStatus(user.kyc.status)" variant="outlined">
                <template #prepend><v-icon :icon="getKycStatusIcon(user.kyc.status)" size="large" /></template>
                <div class="text-black">
                  <p class="text-label font-weight-bold">{{ t('views.updateUser.status') }}</p>
                  <p class="text-value">{{ formatKnownOrRaw('views.userList.kyc.status', user.kyc.status) }}</p>
                </div>
              </v-alert>
            </v-col>

            <v-col cols="12" v-if="user.kyc.risk">
              <v-alert color="orange" variant="outlined">
                <template #prepend><v-icon icon="PhWarningCircle" size="large" /></template>
                <div class="text-black">
                  <p class="text-body-1 font-weight-bold">{{ t('views.updateUser.revision_reason') }}</p>
                  <p class="text-body-1">{{ user.kyc.risk.reason }}</p>
                  <div class="d-flex align-center flex-wrap ga-3">
                    <span class="text-body-2 text-medium-emphasis">{{ t('views.updateUser.code') + ' :' }}</span>
                    <v-chip size="small" variant="flat" color="grey-lighten-3" text-color="black" class="font-weight-bold text-body-1">
                      {{ user.kyc.risk.code }}
                    </v-chip>
                    <span class="text-body-2 text-medium-emphasis">{{ t('views.updateUser.score') + ' :' }}</span>
                    <span class="text-body-1 font-weight-bold">{{ user.kyc.risk.score }}</span>
                  </div>
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <DividerComponent />

          <v-row>
            <v-col cols="12" class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhIdentificationCard" />
              <h2 class="subtitle">{{ t('views.detailUser.overview.subtitleVerifiedData') }}</h2>
            </v-col>
          </v-row>

          <v-row v-if="!user.kyc.verifiedData">
            <v-col cols="12">
              <v-alert color="orange" variant="outlined">
                {{ t('views.detailUser.overview.verifiedDataPending') }}
              </v-alert>
            </v-col>
          </v-row>

          <template v-else>
            <v-row class="details">
              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.identity.firstName') }}</p>
                <p class="text-value">{{ formatItemValue(user.kyc.verifiedData.identity.firstName) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.identity.lastName') }}</p>
                <p class="text-value">{{ formatItemValue(user.kyc.verifiedData.identity.lastName) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.identity.birthDate') }}</p>
                <p class="text-value">{{ formatDateOnly(user.kyc.verifiedData.identity.birthDate) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.identity.birthPlace') }}</p>
                <p class="text-value">{{ formatItemValue(user.kyc.verifiedData.identity.birthPlace) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.identity.gender') }}</p>
                <p class="text-value">{{ formatItemValue(user.kyc.verifiedData.identity.gender) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.identity.nationality') }}</p>
                <p class="text-value">{{ formatItemValue(user.kyc.verifiedData.identity.nationality) }}</p>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12"><p class="text-label font-weight-bold">{{ t('views.detailUser.overview.documentDetails.title') }}</p></v-col>
            </v-row>
            <v-row class="details">
              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.documentDetails.documentType') }}</p>
                <p class="text-value">{{ formatItemValue(user.kyc.verifiedData.documentDetails.documentType) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.documentDetails.documentNumber') }}</p>
                <p class="text-value">{{ formatItemValue(user.kyc.verifiedData.documentDetails.documentNumber) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.documentDetails.issuingCountry') }}</p>
                <p class="text-value">{{ formatItemValue(user.kyc.verifiedData.documentDetails.issuingCountry) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.documentDetails.issueDate') }}</p>
                <p class="text-value">{{ formatDateOnly(user.kyc.verifiedData.documentDetails.issueDate) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.documentDetails.expiryDate') }}</p>
                <p class="text-value">{{ formatDateOnly(user.kyc.verifiedData.documentDetails.expiryDate) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.overview.addressVerification.fullAddress') }}</p>
                <p class="text-value">{{ formatItemValue(user.kyc.verifiedData.addressVerification.fullAddress) }}</p>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12"><p class="text-label font-weight-bold mb-2">{{ t('views.detailUser.amlCheck.title') }}</p></v-col>
              <v-col cols="12" md="4">
                <v-chip :color="amlColor(user.kyc.verifiedData.amlCheck.pepStatus)" text-color="white">
                  {{ t('views.detailUser.amlCheck.pepStatus') }} : {{ formatKnownOrRaw('views.detailUser.amlCheck', user.kyc.verifiedData.amlCheck.pepStatus) }}
                </v-chip>
              </v-col>
              <v-col cols="12" md="4">
                <v-chip :color="amlColor(user.kyc.verifiedData.amlCheck.sanctionsList)" text-color="white">
                  {{ t('views.detailUser.amlCheck.sanctionsList') }} : {{ formatKnownOrRaw('views.detailUser.amlCheck', user.kyc.verifiedData.amlCheck.sanctionsList) }}
                </v-chip>
              </v-col>
              <v-col cols="12" md="4">
                <v-chip :color="amlColor(user.kyc.verifiedData.amlCheck.adverseMedia)" text-color="white">
                  {{ t('views.detailUser.amlCheck.adverseMedia') }} : {{ formatKnownOrRaw('views.detailUser.amlCheck', user.kyc.verifiedData.amlCheck.adverseMedia) }}
                </v-chip>
              </v-col>
            </v-row>
          </template>
        </template>

        <template v-else>
          <v-row>
            <v-col cols="12" class="d-flex justify-between">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="primary" icon="PhShieldWarning" />
                {{ t('views.updateUser.dataleon') }}
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-alert color="orange" variant="outlined">{{ t('views.updateUser.noKyc') }}</v-alert>
            </v-col>
          </v-row>
        </template>

        <DividerComponent />

        <v-row>
          <v-col cols="12">
            <div class="d-flex justify-end ga-3 flex-wrap">
              <v-btn
                v-if="user.status === 'active'"
                :disabled="loading"
                :loading="loading"
                color="error"
                variant="outlined"
                @click="updateStatus('inactive')"
              >
                {{ t('views.updateUser.buttons.deactivate') }}
                <template #prepend><v-icon size="small" icon="PhProhibit" /></template>
              </v-btn>

              <v-btn
                v-if="user.status === 'inactive'"
                :disabled="loading"
                :loading="loading"
                @click="updateStatus('active')"
              >
                {{ t('views.updateUser.buttons.reactivate') }}
                <template #prepend><v-icon size="small" icon="PhCheckCircle" /></template>
              </v-btn>

              <v-btn
                v-if="user.kyc && user.kyc.status !== 'approved'"
                :disabled="loading"
                :loading="loading"
                variant="outlined"
                @click="newKycRequest()"
              >
                {{ t('views.updateUser.buttons.dataleon') }}
                <template #prepend><v-icon icon="PhIdentificationCard" /></template>
              </v-btn>

              <v-menu v-if="!user.accountActivated && hasPermission(['EDIT_USER'])">
                <template #activator="{ props }">
                  <v-btn
                    :disabled="resendingActivation"
                    :loading="resendingActivation"
                    variant="outlined"
                    v-bind="props"
                  >
                    {{ t('views.updateUser.buttons.resendActivation') }}
                    <template #prepend><v-icon size="small" icon="PhPaperPlaneTilt" /></template>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item @click.prevent="resendActivation(false)">
                    <template #default>
                      <p class="text-subtitle-2">{{ t('views.updateUser.buttons.resendActivationMobile') }}</p>
                    </template>
                    <template #prepend><v-icon size="small" icon="PhDeviceMobile" /></template>
                  </v-list-item>
                  <v-list-item @click.prevent="resendActivation(true)">
                    <template #default>
                      <p class="text-subtitle-2">{{ t('views.updateUser.buttons.resendActivationWeb') }}</p>
                    </template>
                    <template #prepend><v-icon size="small" icon="PhGlobe" /></template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- STEP 2: LCB-FT -->
      <v-stepper-window-item :value="2">
        <v-row v-if="!lcbft" align="center" justify="space-between">
          <v-col cols="12" md="auto">
            <v-alert color="grey-lighten-1" variant="outlined">{{ t('views.detailUser.lcbft.empty') }}</v-alert>
          </v-col>
          <v-col cols="12" md="auto" class="text-right d-flex ga-3 justify-end flex-wrap">
            <v-btn
              v-if="hasPermission(['EDIT_USER'])"
              :disabled="editingComplianceFlags"
              :loading="editingComplianceFlags"
              variant="outlined"
              @click="editComplianceFlags()"
            >
              {{ t('views.detailUser.lcbft.editFlags') }}
              <template #prepend><v-icon icon="PhFlag" /></template>
            </v-btn>
            <v-btn :disabled="recomputingLcbft" :loading="recomputingLcbft" color="primary" variant="outlined" @click="recomputeLcbft()">
              {{ t('views.detailUser.lcbft.recompute') }}
              <template #prepend><v-icon icon="PhArrowsClockwise" /></template>
            </v-btn>
          </v-col>
        </v-row>

        <template v-else>
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="auto" class="d-flex align-center ga-3">
              <v-chip :color="formatLcbftClassification(lcbft.classification)" text-color="white" size="large">
                {{ formatKnownOrRaw('views.detailUser.lcbft.classification', lcbft.classification) }}
              </v-chip>
              <span class="text-value">{{ t('views.detailUser.lcbft.totalScore') }} : {{ lcbft.totalScore }}</span>
            </v-col>
            <v-col cols="12" md="auto" class="d-flex align-center ga-4 flex-wrap">
              <span class="text-label">{{ t('views.detailUser.lcbft.computedAt') }} : {{ formatFullDate(lcbft.computedAt) }}</span>
              <v-btn
                v-if="hasPermission(['EDIT_USER'])"
                :disabled="editingComplianceFlags"
                :loading="editingComplianceFlags"
                variant="outlined"
                @click="editComplianceFlags()"
              >
                {{ t('views.detailUser.lcbft.editFlags') }}
                <template #prepend><v-icon icon="PhFlag" /></template>
              </v-btn>
              <v-btn :disabled="recomputingLcbft" :loading="recomputingLcbft" color="primary" variant="outlined" @click="recomputeLcbft()">
                {{ t('views.detailUser.lcbft.recompute') }}
                <template #prepend><v-icon icon="PhArrowsClockwise" /></template>
              </v-btn>
            </v-col>
          </v-row>

          <DividerComponent />

          <v-row class="details">
            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.lcbft.isPep') }}</p>
              <p class="text-value">{{ boolLabel(lcbft.isPep) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.lcbft.isSanctioned') }}</p>
              <p class="text-value">{{ boolLabel(lcbft.isSanctioned) }}</p>
            </v-col>
          </v-row>

          <v-row v-if="lcbft.missingData.length > 0">
            <v-col cols="12">
              <p class="text-label mb-2">{{ t('views.detailUser.lcbft.missingData') }}</p>
              <v-chip v-for="item in lcbft.missingData" :key="item" class="mr-2 mb-2" color="orange" variant="outlined">
                {{ item }}
              </v-chip>
            </v-col>
          </v-row>

          <v-row v-if="lcbft.warnings.length > 0">
            <v-col cols="12">
              <p class="text-label mb-2">{{ t('views.detailUser.lcbft.warnings') }}</p>
              <v-chip v-for="item in lcbft.warnings" :key="item" class="mr-2 mb-2" color="warning" variant="outlined">
                {{ item }}
              </v-chip>
            </v-col>
          </v-row>

          <DividerComponent />

          <v-row>
            <v-col>
              <v-data-table :headers="breakdownHeaders" :items="lcbft.breakdown" :items-per-page="-1" :no-data-text="t('common.noResult')" class="rounded-std" hover>
                <template #item="{ item }">
                  <tr>
                    <td>{{ item.criterion }}</td>
                    <td>{{ item.value }}</td>
                    <td>
                      <v-chip :color="formatLcbftClassification(item.level)" text-color="white" size="small">
                        {{ formatKnownOrRaw('views.detailUser.lcbft.classification', item.level) }}
                      </v-chip>
                    </td>
                    <td class="text-right">{{ item.points ?? '-' }}</td>
                  </tr>
                </template>
                <template #bottom />
              </v-data-table>
            </v-col>
          </v-row>
        </template>
      </v-stepper-window-item>

      <!-- STEP 3: Questionnaire client -->
      <v-stepper-window-item :value="3">
        <v-row v-if="!clientDeclaration">
          <v-col cols="12">
            <v-alert color="grey-lighten-1" variant="outlined">{{ t('views.detailUser.clientDeclaration.empty') }}</v-alert>
          </v-col>
        </v-row>

        <template v-else>
          <v-row>
            <v-col cols="12" class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhUser" />
              <h2 class="subtitle">{{ t('views.detailUser.clientDeclaration.subtitle.identity') }}</h2>
            </v-col>
          </v-row>
          <v-row class="details">
            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.clientType') }}</p>
              <p class="text-value">{{ cd('clientType', clientDeclaration.clientType) }}</p>
            </v-col>

            <v-col v-if="clientDeclaration.clientType === 'LEGAL_ENTITY'" cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.legalEntityType') }}</p>
              <p class="text-value">{{ cd('legalEntityType', clientDeclaration.legalEntityType) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.countryOfResidence') }}</p>
              <p class="text-value">{{ formatItemValue(clientDeclaration.countryOfResidence) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.job') }}</p>
              <p class="text-value">{{ cd('job', clientDeclaration.job) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.profession') }}</p>
              <p class="text-value">{{ formatItemValue(clientDeclaration.profession) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.professionalSector') }}</p>
              <p class="text-value">{{ cd('professionalSector', clientDeclaration.professionalSector) }}</p>
            </v-col>
          </v-row>

          <template v-if="clientDeclaration.clientType === 'LEGAL_ENTITY'">
            <v-row>
              <v-col cols="12"><p class="text-label font-weight-bold">{{ t('views.detailUser.clientDeclaration.legalEntity.title') }}</p></v-col>
            </v-row>
            <v-row class="details">
              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.clientDeclaration.legalEntity.name') }}</p>
                <p class="text-value">{{ formatItemValue(clientDeclaration.legalEntityName) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.clientDeclaration.legalEntity.address') }}</p>
                <p class="text-value">{{ formatItemValue(clientDeclaration.legalEntityAddress) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.clientDeclaration.legalEntity.rcsNumber') }}</p>
                <p class="text-value">{{ formatItemValue(clientDeclaration.legalEntityRcsNumber) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.clientDeclaration.legalEntity.representativeName') }}</p>
                <p class="text-value">{{ formatItemValue(clientDeclaration.legalRepresentativeName) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.clientDeclaration.legalEntity.hasClearBeneficialOwner') }}</p>
                <p class="text-value">{{ boolLabel(clientDeclaration.hasClearBeneficialOwner) }}</p>
              </v-col>
            </v-row>
          </template>

          <DividerComponent />

          <v-row>
            <v-col cols="12" class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhUsersThree" />
              <h2 class="subtitle">{{ t('views.detailUser.clientDeclaration.subtitle.family') }}</h2>
            </v-col>
          </v-row>
          <v-row class="details">
            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.maritalStatus') }}</p>
              <p class="text-value">{{ cd('maritalStatus', clientDeclaration.maritalStatus) }}</p>
            </v-col>

            <v-col v-if="clientDeclaration.maritalStatus === 'MARRIED' || clientDeclaration.maritalStatus === 'PACS'" cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.matrimonialRegime') }}</p>
              <p class="text-value">{{ cd('matrimonialRegime', clientDeclaration.matrimonialRegime) }}</p>
            </v-col>
          </v-row>

          <template v-if="clientDeclaration.partnerLastName || clientDeclaration.partnerFirstName">
            <v-row>
              <v-col cols="12"><p class="text-label font-weight-bold">{{ t('views.detailUser.clientDeclaration.partner.title') }}</p></v-col>
            </v-row>
            <v-row class="details">
              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.clientDeclaration.partner.lastName') }}</p>
                <p class="text-value">{{ formatItemValue(clientDeclaration.partnerLastName) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.clientDeclaration.partner.firstName') }}</p>
                <p class="text-value">{{ formatItemValue(clientDeclaration.partnerFirstName) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.clientDeclaration.partner.birthDate') }}</p>
                <p class="text-value">{{ formatDateOnly(clientDeclaration.partnerBirthDate) }}</p>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <p class="text-label">{{ t('views.detailUser.clientDeclaration.partner.profession') }}</p>
                <p class="text-value">{{ formatItemValue(clientDeclaration.partnerProfession) }}</p>
              </v-col>
            </v-row>
          </template>

          <v-row v-if="clientDeclaration.children.length > 0">
            <v-col cols="12"><p class="text-label font-weight-bold mb-2">{{ t('views.detailUser.clientDeclaration.children.title') }}</p></v-col>
            <v-col cols="12">
              <v-data-table :headers="childrenHeaders" :items="clientDeclaration.children" :items-per-page="-1" :no-data-text="t('common.noResult')" class="rounded-std" hover>
                <template #item="{ item }">
                  <tr>
                    <td>{{ item.lastName }}</td>
                    <td>{{ item.firstName }}</td>
                    <td>{{ formatDateOnly(item.birthDate) }}</td>
                  </tr>
                </template>
                <template #bottom />
              </v-data-table>
            </v-col>
          </v-row>

          <DividerComponent />

          <v-row>
            <v-col cols="12" class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhMoney" />
              <h2 class="subtitle">{{ t('views.detailUser.clientDeclaration.subtitle.finances') }}</h2>
            </v-col>
          </v-row>
          <v-row class="details">
            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.fundsOrigin') }}</p>
              <p class="text-value">{{ cd('fundsOrigin', clientDeclaration.fundsOrigin) }}</p>
            </v-col>

            <v-col v-if="clientDeclaration.fundsOrigin === 'OTHER'" cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.fundsOriginOtherDetail') }}</p>
              <p class="text-value">{{ formatItemValue(clientDeclaration.fundsOriginOtherDetail) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.wealthOrigin') }}</p>
              <p class="text-value">{{ cd('wealthOrigin', clientDeclaration.wealthOrigin) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.annualIncomeAmount') }}</p>
              <p class="text-value">{{ clientDeclaration.annualIncomeAmount != null ? formatCurrency(clientDeclaration.annualIncomeAmount * 100) : t('common.noData') }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.householdIncomeAmount') }}</p>
              <p class="text-value">{{ clientDeclaration.householdIncomeAmount != null ? formatCurrency(clientDeclaration.householdIncomeAmount * 100) : t('common.noData') }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.incomeSources') }}</p>
              <p class="text-value">
                <span v-if="clientDeclaration.incomeSources.length === 0">{{ t('common.noData') }}</span>
                <span v-else>{{ clientDeclaration.incomeSources.map(s => cd('incomeSource', s.incomeSource)).join(', ') }}</span>
              </p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.annualChargesAmount') }}</p>
              <p class="text-value">{{ clientDeclaration.annualChargesAmount != null ? formatCurrency(clientDeclaration.annualChargesAmount * 100) : t('common.noData') }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.monthlySavingsAmount') }}</p>
              <p class="text-value">{{ clientDeclaration.monthlySavingsAmount != null ? formatCurrency(clientDeclaration.monthlySavingsAmount * 100) : t('common.noData') }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.netWorthAmount') }}</p>
              <p class="text-value">{{ clientDeclaration.netWorthAmount != null ? formatCurrency(clientDeclaration.netWorthAmount * 100) : t('common.noData') }}</p>
            </v-col>
          </v-row>

          <v-row v-if="clientDeclaration.credits.length > 0">
            <v-col cols="12"><p class="text-label font-weight-bold mb-2">{{ t('views.detailUser.clientDeclaration.credits.title') }}</p></v-col>
            <v-col cols="12">
              <v-data-table :headers="creditsHeaders" :items="clientDeclaration.credits" :items-per-page="-1" :no-data-text="t('common.noResult')" class="rounded-std" hover>
                <template #item="{ item }">
                  <tr>
                    <td>{{ formatCurrency(item.monthlyPayment * 100) }}</td>
                    <td>{{ formatCurrency(item.outstandingCapital * 100) }}</td>
                  </tr>
                </template>
                <template #bottom />
              </v-data-table>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12"><p class="text-label font-weight-bold">{{ t('views.detailUser.clientDeclaration.split.title') }}</p></v-col>
          </v-row>
          <v-row class="details">
            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.split.nonFinancial') }}</p>
              <p class="text-value">{{ clientDeclaration.splitNonFinancialPct != null ? formatPercentage(clientDeclaration.splitNonFinancialPct) + ' %' : t('common.noData') }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.split.usageGoods') }}</p>
              <p class="text-value">{{ clientDeclaration.splitUsageGoodsPct != null ? formatPercentage(clientDeclaration.splitUsageGoodsPct) + ' %' : t('common.noData') }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.split.placementSavings') }}</p>
              <p class="text-value">{{ clientDeclaration.splitPlacementSavingsPct != null ? formatPercentage(clientDeclaration.splitPlacementSavingsPct) + ' %' : t('common.noData') }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.split.traditional') }}</p>
              <p class="text-value">{{ clientDeclaration.splitTraditionalPct != null ? formatPercentage(clientDeclaration.splitTraditionalPct) + ' %' : t('common.noData') }}</p>
            </v-col>
          </v-row>

          <DividerComponent />

          <v-row>
            <v-col cols="12" class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhGraduationCap" />
              <h2 class="subtitle">{{ t('views.detailUser.clientDeclaration.subtitle.experience') }}</h2>
            </v-col>
          </v-row>
          <v-row class="details">
            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.hasStockMarketExperience') }}</p>
              <p class="text-value">{{ boolLabel(clientDeclaration.hasStockMarketExperience) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.hasFinancialSectorExperience') }}</p>
              <p class="text-value">{{ boolLabel(clientDeclaration.hasFinancialSectorExperience) }}</p>
            </v-col>

            <v-col v-if="clientDeclaration.hasFinancialSectorExperience" cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.financialSectorExperienceRole') }}</p>
              <p class="text-value">{{ formatItemValue(clientDeclaration.financialSectorExperienceRole) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.financialInstrumentsKnowledge') }}</p>
              <p class="text-value">{{ cd('financialInstrumentsKnowledge', clientDeclaration.financialInstrumentsKnowledge) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.priorServices') }}</p>
              <p class="text-value">
                <span v-if="clientDeclaration.priorServices.length === 0">{{ t('common.noData') }}</span>
                <span v-else>{{ clientDeclaration.priorServices.map(s => cd('priorService', s)).join(', ') }}</span>
              </p>
            </v-col>

            <v-col v-if="clientDeclaration.priorServices.includes(PriorServiceEnum.OTHER)" cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.priorServicesOtherDetail') }}</p>
              <p class="text-value">{{ formatItemValue(clientDeclaration.priorServicesOtherDetail) }}</p>
            </v-col>

            <v-col v-if="clientDeclaration.priorServices.includes(PriorServiceEnum.DISCRETIONARY_MANAGEMENT)" cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.managedAssetCategory') }}</p>
              <p class="text-value">{{ cd('managedAssetCategory', clientDeclaration.managedAssetCategory) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.cryptoAdviceOutcome') }}</p>
              <p class="text-value">{{ cd('cryptoAdviceOutcome', clientDeclaration.cryptoAdviceOutcome) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.cryptoAllocationRatio') }}</p>
              <p class="text-value">{{ cd('cryptoAllocationRatio', clientDeclaration.cryptoAllocationRatio) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.wealthInvestmentRatio') }}</p>
              <p class="text-value">{{ cd('wealthInvestmentRatio', clientDeclaration.wealthInvestmentRatio) }}</p>
            </v-col>

            <v-col cols="12">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.financialTransactionsDetail') }}</p>
              <p class="text-value">{{ formatItemValue(clientDeclaration.financialTransactionsDetail) }}</p>
            </v-col>
          </v-row>

          <DividerComponent />

          <v-row>
            <v-col cols="12" class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhShieldCheck" />
              <h2 class="subtitle">{{ t('views.detailUser.clientDeclaration.subtitle.compliance') }}</h2>
            </v-col>
          </v-row>
          <v-row class="details">
            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.incomeCoversCharges') }}</p>
              <p class="text-value">{{ cd('incomeCoversCharges', clientDeclaration.incomeCoversCharges) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.guaranteeDisclaimerAcknowledged') }}</p>
              <p class="text-value">{{ boolLabel(clientDeclaration.guaranteeDisclaimerAcknowledged) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.distributionChannel') }}</p>
              <p class="text-value">{{ cd('distributionChannel', clientDeclaration.distributionChannel) }}</p>
            </v-col>
          </v-row>

          <DividerComponent />

          <v-row>
            <v-col cols="12" class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhWallet" />
              <h2 class="subtitle">{{ t('views.detailUser.clientDeclaration.subtitle.investment') }}</h2>
            </v-col>
          </v-row>
          <v-row class="details">
            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.investedAmount') }}</p>
              <p class="text-value">{{ clientDeclaration.investedAmount != null ? formatCurrency(clientDeclaration.investedAmount * 100) : t('common.noData') }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.clientDeclaration.paymentMethod') }}</p>
              <p class="text-value">{{ cd('paymentMethod', clientDeclaration.paymentMethod) }}</p>
            </v-col>
          </v-row>
        </template>
      </v-stepper-window-item>

      <!-- STEP 4: Questionnaire investisseur -->
      <v-stepper-window-item :value="4">
        <v-row v-if="!investorForm">
          <v-col cols="12">
            <v-alert color="grey-lighten-1" variant="outlined">{{ t('views.detailUser.investorForm.empty') }}</v-alert>
          </v-col>
        </v-row>

        <template v-else>
          <v-row class="details">
            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.investorForm.scoreTotal') }}</p>
              <p class="text-value">{{ investorForm.scoreTotal }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.investorForm.profileType') }}</p>
              <p class="text-value">{{ formatItemValue(investorForm.profileType) }}</p>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <p class="text-label">{{ t('views.detailUser.investorForm.nextAllowedDate') }}</p>
              <p class="text-value">{{ formatFullDate(investorForm.nextAllowedDate) }}</p>
            </v-col>
          </v-row>

          <DividerComponent />

          <v-row>
            <v-col cols="12">
              <p class="text-label mb-2">{{ t('views.detailUser.investorForm.contributionHint') }}</p>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-data-table :headers="answersHeaders" :items="investorForm.answers" :items-per-page="-1" :no-data-text="t('common.noResult')" class="rounded-std" hover>
                <template #item="{ item }">
                  <tr>
                    <td>{{ item.question }}</td>
                    <td>{{ item.answer }}</td>
                    <td>{{ formatItemValue(item.category) }}</td>
                    <td class="text-right"><span class="font-weight-bold">{{ item.weightedContribution ?? '-' }}</span></td>
                    <td class="text-right text-medium-emphasis">{{ item.weight }}</td>
                  </tr>
                </template>
                <template #bottom />
              </v-data-table>
            </v-col>
          </v-row>
        </template>
      </v-stepper-window-item>

      <!-- STEP 5: Documents KYC -->
      <v-stepper-window-item :value="5">
        <v-row v-if="kycDocuments.length === 0">
          <v-col cols="12">
            <v-alert color="grey-lighten-1" variant="outlined">{{ t('views.detailUser.kycDocuments.empty') }}</v-alert>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col>
            <v-data-table :headers="documentsHeaders" :items="kycDocuments" :items-per-page="-1" :no-data-text="t('common.noResult')" class="rounded-std" hover>
              <template #item="{ item }">
                <tr>
                  <td>{{ item.name }}</td>
                  <td>{{ item.documentType }}</td>
                  <td>{{ formatKnownOrRaw('views.userList.kyc.state', item.state) }}</td>
                  <td>{{ formatKnownOrRaw('views.userList.kyc.status', item.status) }}</td>
                  <td>
                    <v-btn
                      variant="plain"
                      :disabled="!item.downloadUrl"
                      @click.stop.prevent="downloadKycDocument(item)"
                    >
                      <v-icon icon="PhDownload" />
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template #bottom />
            </v-data-table>
          </v-col>
        </v-row>
      </v-stepper-window-item>
    </v-stepper-window>
  </v-stepper>
</template>

<style scoped>
.text-label {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 2px;
}

.text-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
}

.details .v-col {
  padding-top: 10px;
  padding-bottom: 10px;
}
</style>
